import type { TranslationKey } from "@/config/translationKeys";

/**
 * Core domain types shared by the whole application.
 *
 * Everything in `src/domain` is framework-free and side-effect free so it can
 * be unit tested in isolation and reused by both React components and data
 * loaders.
 */

/** Built-in UI languages. Admins may register more languages at runtime. */
export type BuiltInLanguage = "no" | "en";
export type LanguageCode = string;

/** A label with Norwegian and English text, optionally more languages. */
export interface LocalizedLabel {
  no: string;
  en: string;
  [language: string]: string;
}

export type ViewType = "numericPrefix" | "categorical";
export type ViewNormalizer = "default" | "resource";
export type ChipDisplay = "hidden" | "view" | "objectives";
export type ValuePattern = "solid" | "striped";
export type ValueTextColor = "auto" | "#000000" | "#ffffff";

/** One classification value (e.g. evidence level "1") within a view. */
export interface ViewValue {
  label: LocalizedLabel;
  color: string;
  pattern: ValuePattern;
  stripeColor: string;
  stripeBackground: string;
  textColor: ValueTextColor;
}

/**
 * A matrix "view": a classification dimension read from one source column.
 * This is the persisted shape (`matrix_settings.views`) and the runtime shape.
 */
export interface View {
  id: string;
  column: string;
  type: ViewType;
  normalizer: ViewNormalizer;
  label: LocalizedLabel;
  columnLayoutId: string;
  detailsViewIds: string[];
  chipDisplay: ChipDisplay;
  visible: boolean;
  sortOrder: number;
  /** Value codes in display order. */
  order: string[];
  values: Record<string, ViewValue>;
}

/** Legacy view definition format used for the built-in defaults. */
export interface LegacyViewDefinition {
  id: string;
  columnLayoutId: string;
  detailsViewIds: string[];
  chipDisplay: ChipDisplay;
  label: LocalizedLabel;
  column: string;
  type: ViewType;
  classPrefix: string;
  order?: string[];
  normalize?: "resource";
  values: Record<string, { label: LocalizedLabel; color: string; extraCSS?: string }>;
}

export interface AgeGroup {
  key: string;
  label: LocalizedLabel;
  min: number;
  max: number;
}

export interface Arena {
  key: string;
  label: LocalizedLabel;
  sortOrder: number;
}

export interface PredefinedCategory {
  key: string;
  label: LocalizedLabel;
}

export interface ColumnLayout {
  id: string;
  type: "ageGroups" | "categoryFromRow";
  /** Source column for `categoryFromRow` layouts. */
  column?: string;
  /** Optional view whose parsed code is preferred when matching categories. */
  sourceViewId?: string;
  palette: string;
  statsTitleKey: TranslationKey;
  stackedStatsTitleKey: TranslationKey;
  predefinedCategories?: PredefinedCategory[];
}

export interface ColumnLayoutConfig {
  defaultLayoutId: string;
  layouts: Record<string, ColumnLayout>;
}

/** A resolved matrix column (an age group or a category). */
export interface ColumnDefinition {
  key: string;
  label: LocalizedLabel;
  /** Age range such as "3-5"; empty for categories. */
  tooltip: string;
}

export type ExtraSectionStyle = "default" | "m" | "h" | "t";

/** Free-form detail section read from `Data#Title`-style source columns. */
export interface ExtraSection {
  title: string;
  text: string;
  style: ExtraSectionStyle;
  /** Restricts the section to one language; `null` shows it for all. */
  lang: BuiltInLanguage | null;
}

export interface ObjectiveValue {
  label: LocalizedLabel;
  color: string;
}

export type InterventionStatus = "draft" | "submitted" | "approved";

/** A raw source row: an Excel row or the `row_data` JSON of a DB row. */
export type SourceRow = Record<string, unknown>;

/** A parsed intervention as used by every view of the application. */
export interface Intervention {
  /** Stable client id (`Tiltak <itemNumber>` or the database UUID). */
  id: string;
  itemNumber: string;
  display: { no: string; en: string };
  description: { no: string; en: string };
  /** Parsed value code per view id ("" when unclassified). */
  codes: Record<string, string>;
  /** Raw cell text per view id. */
  raw: Record<string, string>;
  objectiveCodes: string[];
  links: string[];
  extraSections: ExtraSection[];
  arena: string;
  region: string;
  youngest: number | null;
  oldest: number | null;
  ageGroupKeys: string[];
  /** Matrix column keys per column layout id. */
  columnLayoutKeys: Record<string, string[]>;
  sourceRow: SourceRow;
  /** Database-only fields (individual auth mode). */
  dbId?: string;
  status?: InterventionStatus;
  isActive?: boolean;
  submittedBy?: string | null;
  createdAt?: string | null;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  /** Questionnaire the intervention was created with; `null` for older or imported rows. */
  questionnaire?: { id: string; version: number } | null;
  location?: string;
}

/** Result of parsing a set of source rows. */
export interface InterventionDataset {
  items: Intervention[];
  regions: string[];
  objectives: Record<string, ObjectiveValue>;
  /** Value codes present per view id, including the unclassified marker. */
  availableViewCodes: Record<string, Set<string>>;
  columnDefsByLayout: Record<string, ColumnDefinition[]>;
  sourceRows: SourceRow[];
}

export const UNCLASSIFIED_CODE = "__unclassified__";
export const OBJECTIVE_NONE_CODE = "__none__";
